import { create } from 'zustand'

export type ThemeId =
  | 'ember'
  | 'midnight'
  | 'solar'
  | 'forest'
  | 'mocha'
  | 'aurora'
  | 'synthwave'
  | 'nordic'
  | 'vampire'
  | 'sakura'
  | 'matrix'
  | 'coral'

export interface ThemeMeta {
  id: ThemeId
  label: string
  description: string
  preview: {
    bg: string
    surface: string
    text: string
    accent: string
  }
}

export const THEMES: ThemeMeta[] = [
  {
    id: 'ember',
    label: 'Ember Dark',
    description: 'Warmes Dark mit gluthaft-orangem Akzent. Default-Theme.',
    preview: {
      bg: '#0A0A0B',
      surface: '#16100E',
      text: '#F5F0EB',
      accent: '#FF6B35',
    },
  },
  {
    id: 'midnight',
    label: 'Midnight Slate',
    description: 'Kühles Dark mit Stahl-Blau-Akzent. Klar und ruhig.',
    preview: {
      bg: '#0B1220',
      surface: '#121B2E',
      text: '#E2E8F0',
      accent: '#38BDF8',
    },
  },
  {
    id: 'solar',
    label: 'Solar Light',
    description: 'Helles Pergament-Theme mit Terracotta-Akzent.',
    preview: {
      bg: '#FAF6F1',
      surface: '#F0E9E0',
      text: '#1E1B18',
      accent: '#C2410C',
    },
  },
  {
    id: 'forest',
    label: 'Forest Deep',
    description: 'Tiefes Waldgrün mit moosfarbenem Akzent. Beruhigend und fokussiert.',
    preview: {
      bg: '#08120E',
      surface: '#0E1C16',
      text: '#E9F0E6',
      accent: '#84CC16',
    },
  },
  {
    id: 'mocha',
    label: 'Mocha Warm',
    description: 'Warmes Pergament mit Espresso-Akzent. Tagsüber angenehm hell.',
    preview: {
      bg: '#F7F0E8',
      surface: '#EBE0D2',
      text: '#291C16',
      accent: '#79461E',
    },
  },
  {
    id: 'aurora',
    label: 'Aurora Glow',
    description: 'Tiefes Indigo mit Magenta-Glow. Vibrant für Nachtarbeit.',
    preview: {
      bg: '#0C0718',
      surface: '#160E29',
      text: '#F0E8FF',
      accent: '#D946EF',
    },
  },
  {
    id: 'synthwave',
    label: 'Synthwave 84',
    description: '80s-Retro-Look in Lila mit knallrosa Akzent — Outrun-Vibes.',
    preview: {
      bg: '#130924',
      surface: '#1E103A',
      text: '#FFEBFA',
      accent: '#FF47A8',
    },
  },
  {
    id: 'nordic',
    label: 'Nordic Frost',
    description: 'Minimalistisches Light in Eis-Slate mit kühlem Blau-Akzent.',
    preview: {
      bg: '#F4F8FB',
      surface: '#E8EEF4',
      text: '#1E293B',
      accent: '#5190BF',
    },
  },
  {
    id: 'vampire',
    label: 'Vampire Crimson',
    description: 'Burgund-Schwarz mit blutrotem Akzent. Dramatisch und düster.',
    preview: {
      bg: '#0E0608',
      surface: '#1C0C10',
      text: '#F6E2E6',
      accent: '#DC263C',
    },
  },
  {
    id: 'sakura',
    label: 'Sakura Bloom',
    description: 'Helles Kirschblüten-Theme mit zartem Rosé. Frühling.',
    preview: {
      bg: '#FEF7F8',
      surface: '#FAEBEE',
      text: '#401E2A',
      accent: '#DB4E7C',
    },
  },
  {
    id: 'matrix',
    label: 'Matrix',
    description: 'Pures Schwarz mit Phosphor-Grün. Hacker-Terminal-Look.',
    preview: {
      bg: '#040804',
      surface: '#08120A',
      text: '#C6FFC8',
      accent: '#00F084',
    },
  },
  {
    id: 'coral',
    label: 'Coral Reef',
    description: 'Sonniges Apricot-Light mit warmem Korall-Akzent.',
    preview: {
      bg: '#FFF6EE',
      surface: '#FCE8D7',
      text: '#40261E',
      accent: '#F46656',
    },
  },
]

const STORAGE_KEY = 'embertime.theme'

const VALID_THEMES: ThemeId[] = [
  'ember',
  'midnight',
  'solar',
  'forest',
  'mocha',
  'aurora',
  'synthwave',
  'nordic',
  'vampire',
  'sakura',
  'matrix',
  'coral',
]

function readStored(): ThemeId {
  if (typeof window === 'undefined') return 'ember'
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw && (VALID_THEMES as string[]).includes(raw)) return raw as ThemeId
  } catch {
    // ignore
  }
  return 'ember'
}

function applyToDocument(id: ThemeId) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = id
  }
}

export function bootTheme(): void {
  applyToDocument(readStored())
}

interface ThemeStore {
  theme: ThemeId
  setTheme: (id: ThemeId) => void
}

export const useTheme = create<ThemeStore>((set) => ({
  theme: readStored(),
  setTheme: (id) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, id)
    } catch {
      // ignore quota errors
    }
    applyToDocument(id)
    set({ theme: id })
  },
}))
